import { MODULE_ROUTE } from './routes';
import type {
  SentryFlowBuildOptions,
  SentryFlowDefinition,
  SentryModule,
} from '../types';

const routeHintsFor = (
  module: SentryModule,
  override?: readonly string[],
): readonly string[] => override ?? [MODULE_ROUTE[module]];

const buildFlow = (
  module: SentryModule,
  feature: string,
  event: string | readonly string[] = [],
  options?: SentryFlowBuildOptions,
): SentryFlowDefinition => ({
  module,
  feature,
  event,
  eventPattern: options?.eventPattern,
  notes: options?.notes,
  routeHints: routeHintsFor(module, options?.routes),
});

export const TABLE_FLOW_EVENTS = {
  table_filters: 'Table_Filters:Apply',
  date_filters: 'Table_Date_Filters:Apply',
  export: 'Primary_Button_Export',
  row_menu: 'Table_Row_EllipsisPopup',
  quick_create: 'Quick_Create_Button',
} as const;

type TableOp = keyof typeof TABLE_FLOW_EVENTS;

const tableFlows = (module: SentryModule, ops: readonly TableOp[]) =>
  ops.map(op => buildFlow(module, op, TABLE_FLOW_EVENTS[op]));

export const P0_FLOWS: readonly SentryFlowDefinition[] = [
  buildFlow('landing', 'access_product', 'Landing_Page_Primary_Button_Access', {
    notes: 'PG landing entry',
  }),
  buildFlow('summary', 'dashboard_redirect', 'Primary_Button_Dashboard_Redirect', {
    notes: 'Cross-product CTA',
  }),
  buildFlow('onboarding', 'new_form', 'New_Onboarding_Form', {
    notes: 'Blocks new merchants',
  }),
  buildFlow(
    'payment_links',
    'create_link',
    ['Primary_Button_Create Payment Link', 'Create Payment Link'],
    { notes: 'Core PG revenue' },
  ),
  buildFlow('payment_links', 'create_and_send', 'Primary_Button_Create&Send'),
  buildFlow('payment_links', 'create', 'Primary_Button_Create'),
  buildFlow('payment_links', 'update', 'Primary_Button_Update'),
  buildFlow('payment_links', 'send', 'Primary_Button_Send'),
  buildFlow(
    'payment_forms',
    'create_payment_form',
    'Primary_Button_Create Payment Form',
  ),
  buildFlow('payment_forms', 'create', 'Primary_Button_Create'),
  buildFlow('payment_forms', 'save', 'Primary_Button_Save'),
  buildFlow(
    'payment_buttons',
    'create_button',
    'Primary_Button_CreatePaymentButton',
  ),
  buildFlow('payments', 'refund_modal_submit', 'Primary_Button_Refund_Modal_Submit', {
    routes: ['/transactions/payments', '/transactions/orders'],
  }),
  buildFlow('payments', 'refund_view', 'Primary_Button_Refund'),
  buildFlow('payments', 'initiate_refund_row', 'Table_Row_InitiateRefund'),
  buildFlow('settlements', 'withdraw_now', 'Primary_Button_WithdrawNow'),
  buildFlow('settlements', 'withdraw_confirm', 'Primary_Button_Confirm'),
  buildFlow('settlements', 'activate_instantly', 'Primary_Button_ActivateInstantly'),
  buildFlow('payouts', 'create_transfer', 'Primary_Button', {
    routes: ['/payout', '/transfers'],
  }),
  buildFlow('payouts', 'transfer_2fa', 'Confirm_2FA_Auth', {
    routes: ['/payout', '/transfers'],
  }),
  buildFlow('developers', 'api_key_generated', 'pg_api_key_generated', {
    routes: ['/developers', '/api-keys'],
  }),
  buildFlow('developers', 'api_key_2fa', 'Confirm_2FA_Auth', {
    routes: ['/developers/api-keys', '/api-keys'],
  }),
  buildFlow('developers', 'api_key_download', 'Primary_Button_Download_API_Key', {
    routes: ['/developers', '/api-keys'],
  }),
  buildFlow('developers', 'webhook_add', 'Primary_Button_AddWebhookEndpoint', {
    routes: ['/webhooks', '/developers'],
  }),
  buildFlow('developers', 'webhook_save', 'Primary_Button_Save', {
    routes: ['/webhooks', '/developers/webhooks'],
  }),
  buildFlow(
    'disputes',
    'take_action',
    ['Table_Row_Button_TakeAction', 'Primary_Button_TakeAction'],
    { routes: ['/disputes'] },
  ),
  buildFlow('disputes', 'submit_evidence', 'Primary_Button_SubmitEvidence', {
    routes: ['/disputes'],
  }),
  buildFlow('disputes', 'accept_dispute', 'Primary_Button_AcceptDispute', {
    routes: ['/disputes'],
  }),
  buildFlow(
    'landing_cdb',
    'terms_agree',
    'Primary_Button_Terms_And_Conditions_I_Agree',
    { routes: ['/landing', '/merchants'] },
  ),
  buildFlow('landing_cdb', 'verify_now', 'Primary_Button_Verify_Now', {
    routes: ['/landing', '/merchants'],
  }),
  buildFlow('landing_cdb', 'complete_now', 'Primary_Button_Complete_Now', {
    routes: ['/landing', '/merchants'],
  }),
];

export const P1_FLOWS: readonly SentryFlowDefinition[] = [
  ...tableFlows('payments', ['table_filters', 'date_filters', 'export']),
  ...tableFlows('payment_links', [
    'row_menu',
    'table_filters',
    'date_filters',
    'quick_create',
  ]),
  ...tableFlows('payment_forms', ['row_menu', 'table_filters', 'quick_create']),
  buildFlow('reports', 'generate_new', 'Primary_Button_GenerateNewReport'),
  buildFlow('reports', 'generate', 'Primary_Button_GenerateReport'),
  ...tableFlows('reports', ['row_menu', 'table_filters', 'date_filters']),
  ...tableFlows('orders', ['table_filters', 'date_filters', 'export']),
  ...tableFlows('settlements', ['table_filters', 'date_filters']),
  ...tableFlows('refunds', ['table_filters', 'date_filters']),
  ...tableFlows('disputes', ['table_filters', 'export']),
  ...tableFlows('easy_split', ['table_filters']),
  buildFlow('developers', 'webhook_logs_filters', TABLE_FLOW_EVENTS.table_filters, {
    routes: ['/developers/webhooks/logs'],
  }),
  ...tableFlows('customers', ['date_filters']),
  buildFlow('summary', 'quick_create', TABLE_FLOW_EVENTS.quick_create),
  buildFlow('summary', 'product_tour', 'sage_tour_shown', {
    routes: [
      '/summary',
      '/transactions/payments',
      '/payment-links',
      '/settlements',
    ],
  }),
  buildFlow('settings', 'enable_payment_method', [], {
    eventPattern: /^Primary_Button_Enable_.*_Confirm$/,
    routes: ['/settings/payment-methods'],
    notes: 'Revenue — payment method activation on settings page',
  }),
  buildFlow(
    'offers',
    'create_offer',
    [
      'Primary_Button_CreateNewOffer',
      'OffersV2_Primary_Button_CreateNewOffer',
      'Primary_Button_CreateNewOffer_Unleash',
      'OffersV2_Dropdown_CreateNewOffer',
    ],
    {
      routes: ['/offers'],
      notes: 'Revenue — merchant creates a new offer',
    },
  ),
  buildFlow('offers', 'create_offer_submit', 'Primary_Button_Create', {
    routes: ['/offers/all/offers-description', '/offers/all'],
    notes: 'Revenue — offer creation confirm step',
  }),
  buildFlow('risk_shield', 'enable_network_intelligence', [], {
    eventPattern: /Network[_\s-]*Intelligence/i,
    routes: ['/configurations', '/risk-shield'],
    notes: 'Revenue/Risk — Cashfree Network Intelligence config enable (federated RS)',
  }),
  buildFlow('risk_shield', 'enable_goi_blacklist', [], {
    eventPattern: /Government[_\s-]*of[_\s-]*India.*Blacklist|GOI.*Blacklist/i,
    routes: ['/configurations', '/risk-shield'],
    notes: 'Revenue/Risk — Government of India blacklist config enable (federated RS)',
  }),
  buildFlow(
    'risk_shield',
    'add_to_blacklist',
    'Add_To_Riskshield_Blacklist_Add_Now',
    {
      routes: ['/disputes/dispute-details', '/transactions/disputes'],
      notes: 'Risk Shield — add customer to blacklist from dispute',
    },
  ),
  buildFlow('vba', 'create_virtual_account', 'Primary_Button_CreateVirtualAccount', {
    routes: ['/vba-configuration/accounts'],
    notes: 'Enterprise — VBA account creation (financial services cohort)',
  }),
];
